import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Button, Typography } from '@mui/material';
import { primaryButtonBackgroundColor } from '../../../constants/Colors';
import { headerHeight, spHeaderHeight } from '../../../constants/Location';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import CustomTextfield from '../../uiParts/CustomTextfield';
import Header from '../../uiParts/Header';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { common } from '../../../modules/error';
import { productContactSchema } from '../../../validation/contact/index';
import type { ProductContactType } from '../../../validation/contact/contactType';

import { AuthContext } from '../../../contexts/auth';
import { response } from '../../../api/db';
import { getCustomerInfo } from '../../../api/db/user';
import { sendProductContact } from '../../../api/db/contact';
import { AxiosError } from 'axios';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';

const styles = {
  button: {
    fontSize: '16px',
    fontWeight: '500'
  },
  container: {
    m: `${headerHeight} auto 288px`,
    width: '560px',
    '@media screen and (max-width:600px)': {
      m: `${spHeaderHeight} auto 204px`,
      width: '90%'
    }
  },
  title: {
    fontSize: '32px',
    fontWeight: '500',
    mb: '24px',
    pt: '32px',
    '@media screen and (max-width:600px)': {
      fontSize: '24px',
      pt: '24px'
    }
  },
  primaryButtonBackgroundColor
};

const ProductContact: React.FC = () => {
  const navigate = useNavigate();
  const [errorText, setErrorText] = useState('');
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const { isLoggedIn } = useContext(AuthContext);
  const [userId, setUserId] = useState('');

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch
  } = useForm<ProductContactType>({
    resolver: yupResolver(productContactSchema)
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('company') === undefined ||
        '' ||
        watch('content') === undefined ||
        '' ||
        watch('email') === undefined ||
        '' ||
        watch('tel') === undefined ||
        '' ||
        Boolean(errors.company) ||
        Boolean(errors.content) ||
        Boolean(errors.email) ||
        Boolean(errors.tel)
      );
    }
    return false;
  };

  useEffect(() => {
    void (async () => {
      setLoading(true);
      if (!isLoggedIn) {
        navigate('/auth/login');
        return;
      }

      try {
        // ログイン中のユーザーを取得
        const response = await getCustomerInfo();
        const user = response.data;
        setUserId(user.id);
        // ユーザー情報をフォームにセット
        reset({
          company: user.company,
          department: user.department,
          name: user.last_name + user.first_name,
          email: user.email,
          tel: user.tel
        });
        setDisabled(true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          setErrorText(errorResponse.data.message);
          setOpen(true);
        } else {
          setErrorText(common);
          setOpen(true);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onSubmit = async (): Promise<void> => {
    setLoading(true);
    if (!isLoggedIn) {
      navigate('/auth/login');
      return;
    }
    try {
      if (userId) {
        // お問い合わせを送信
        await sendProductContact(userId, watch);
        navigate('/product/contact/complete');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText(loginErrorResponse.data.message);
        setOpen(true);
      } else {
        setErrorText(common);
        setOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomHead title="建材登録についてお問合せ | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Header />
      <Box component="div" sx={styles.container}>
        <Typography sx={styles.title}>建材登録についてお問い合わせ</Typography>
        <Controller
          render={({ field }) => (
            <CustomTextfield
              {...field}
              disabled={disabled}
              error={Boolean(errors.company)}
              formMb="24px"
              helperText={errors.company?.message}
              labelText="会社名"
              placeholder="サンプルテキスト"
              requiredText="必須"
            />
          )}
          control={control}
          name="company"
        />
        <Controller
          render={({ field }) => (
            <CustomTextfield
              {...field}
              disabled={disabled}
              error={Boolean(errors.company)}
              formMb="24px"
              helperText={errors.company?.message}
              labelText="部署名"
              placeholder="サンプルテキスト"
              requiredText="必須"
            />
          )}
          control={control}
          name="department"
        />
        <Controller
          render={({ field }) => (
            <CustomTextfield
              {...field}
              disabled={disabled}
              error={Boolean(errors.company)}
              formMb="24px"
              helperText={errors.company?.message}
              labelText="担当者名"
              placeholder="サンプルテキスト"
              requiredText="必須"
            />
          )}
          control={control}
          name="name"
        />
        <Controller
          render={({ field }) => (
            <CustomTextfield
              {...field}
              disabled={disabled}
              error={Boolean(errors.email)}
              formMb="24px"
              helperText={errors.email?.message}
              labelText="メールアドレス"
              placeholder="サンプルテキスト"
              requiredText="必須"
            />
          )}
          control={control}
          name="email"
        />
        <Controller
          render={({ field }) => (
            <CustomTextfield
              {...field}
              disabled={disabled}
              error={Boolean(errors.tel)}
              formMb="24px"
              helperText={errors.tel?.message}
              labelText="電話番号"
              placeholder="サンプルテキスト"
              requiredText="必須"
            />
          )}
          control={control}
          name="tel"
        />
        <Controller
          render={({ field }) => (
            <CustomTextfield
              {...field}
              error={Boolean(errors.content)}
              fieldType="textareaRow5"
              formMb="40px"
              helperText={errors.content?.message}
              labelText="お問い合わせの内容"
              multiline
              placeholder="サンプルテキスト"
              requiredText="必須"
              rows={5}
            />
          )}
          control={control}
          name="content"
        />
        <Button
          disabled={buttonDisabledFlag()}
          fullWidth
          sx={[styles.button, styles.primaryButtonBackgroundColor]}
          variant="contained"
          onClick={() => {
            void (async () => {
              setFirstSubmit(true);
              await handleSubmit(async () => await onSubmit())();
            })();
          }}
        >
          送信する
        </Button>
      </Box>
      <CustomSnackbar
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        open={open}
        text={errorText}
      />
    </>
  );
};

export default ProductContact;
